import { openDB, type IDBPDatabase } from 'idb';
import type { CachedRoute, PersistedUiState, Prefs, QueueItem, SessionState } from '../state/types';
import { DEFAULT_PREFS } from '../state/types';

const DB_NAME = 'gedenk-delivery';
const DB_VERSION = 1;
const KV_STORE = 'kv';
const QUEUE_STORE = 'queue';

type KvKey = 'session' | 'route' | 'ui' | 'prefs' | 'seq';

export interface DeliveryStorage {
  readSession(): Promise<SessionState | null>;
  writeSession(value: SessionState | null): Promise<void>;
  readRoute(): Promise<CachedRoute | null>;
  writeRoute(value: CachedRoute | null): Promise<void>;
  readUi(): Promise<PersistedUiState | null>;
  writeUi(value: PersistedUiState): Promise<void>;
  readPrefs(): Promise<Prefs>;
  writePrefs(value: Prefs): Promise<void>;
  nextSeq(): Promise<number>;
  listQueue(): Promise<QueueItem[]>;
  putQueueItem(item: QueueItem): Promise<void>;
  deleteQueueItem(clientActionId: string): Promise<void>;
  clearAll(): Promise<void>;
}

/**
 * IndexedDB-opslag: rit-cache, ui-staat, wachtrij en voorkeuren. Alles wat de bezorger
 * vastlegt staat hier vóór het verstuurd is (README §Interacties, offline-first).
 */
class IndexedDbStorage implements DeliveryStorage {
  private dbPromise: Promise<IDBPDatabase> | null = null;

  private db(): Promise<IDBPDatabase> {
    this.dbPromise ??= openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(KV_STORE)) db.createObjectStore(KV_STORE);
        if (!db.objectStoreNames.contains(QUEUE_STORE)) {
          const store = db.createObjectStore(QUEUE_STORE, { keyPath: 'clientActionId' });
          store.createIndex('seq', 'seq');
        }
      },
    });
    return this.dbPromise;
  }

  private async get<T>(key: KvKey): Promise<T | null> {
    const db = await this.db();
    const value = (await db.get(KV_STORE, key)) as T | undefined;
    return value ?? null;
  }

  private async put(key: KvKey, value: unknown): Promise<void> {
    const db = await this.db();
    if (value === null) await db.delete(KV_STORE, key);
    else await db.put(KV_STORE, value, key);
  }

  readSession(): Promise<SessionState | null> {
    return this.get<SessionState>('session');
  }

  writeSession(value: SessionState | null): Promise<void> {
    return this.put('session', value);
  }

  readRoute(): Promise<CachedRoute | null> {
    return this.get<CachedRoute>('route');
  }

  writeRoute(value: CachedRoute | null): Promise<void> {
    return this.put('route', value);
  }

  readUi(): Promise<PersistedUiState | null> {
    return this.get<PersistedUiState>('ui');
  }

  writeUi(value: PersistedUiState): Promise<void> {
    return this.put('ui', value);
  }

  async readPrefs(): Promise<Prefs> {
    const stored = await this.get<Partial<Prefs>>('prefs');
    return { ...DEFAULT_PREFS, ...(stored ?? {}) };
  }

  writePrefs(value: Prefs): Promise<void> {
    return this.put('prefs', value);
  }

  async nextSeq(): Promise<number> {
    const db = await this.db();
    const tx = db.transaction(KV_STORE, 'readwrite');
    const current = ((await tx.store.get('seq')) as number | undefined) ?? 0;
    const next = current + 1;
    await tx.store.put(next, 'seq');
    await tx.done;
    return next;
  }

  async listQueue(): Promise<QueueItem[]> {
    const db = await this.db();
    const items = (await db.getAllFromIndex(QUEUE_STORE, 'seq')) as QueueItem[];
    return items;
  }

  async putQueueItem(item: QueueItem): Promise<void> {
    const db = await this.db();
    await db.put(QUEUE_STORE, item);
  }

  async deleteQueueItem(clientActionId: string): Promise<void> {
    const db = await this.db();
    await db.delete(QUEUE_STORE, clientActionId);
  }

  async clearAll(): Promise<void> {
    const db = await this.db();
    await db.clear(KV_STORE);
    await db.clear(QUEUE_STORE);
  }
}

/** Terugval voor omgevingen zonder IndexedDB (privémodus, oude WebView). */
export class MemoryStorage implements DeliveryStorage {
  private session: SessionState | null = null;
  private route: CachedRoute | null = null;
  private ui: PersistedUiState | null = null;
  private prefs: Prefs = { ...DEFAULT_PREFS };
  private seq = 0;
  private queue = new Map<string, QueueItem>();

  readSession(): Promise<SessionState | null> {
    return Promise.resolve(this.session);
  }

  writeSession(value: SessionState | null): Promise<void> {
    this.session = value;
    return Promise.resolve();
  }

  readRoute(): Promise<CachedRoute | null> {
    return Promise.resolve(this.route);
  }

  writeRoute(value: CachedRoute | null): Promise<void> {
    this.route = value;
    return Promise.resolve();
  }

  readUi(): Promise<PersistedUiState | null> {
    return Promise.resolve(this.ui);
  }

  writeUi(value: PersistedUiState): Promise<void> {
    this.ui = value;
    return Promise.resolve();
  }

  readPrefs(): Promise<Prefs> {
    return Promise.resolve({ ...this.prefs });
  }

  writePrefs(value: Prefs): Promise<void> {
    this.prefs = { ...value };
    return Promise.resolve();
  }

  nextSeq(): Promise<number> {
    this.seq += 1;
    return Promise.resolve(this.seq);
  }

  listQueue(): Promise<QueueItem[]> {
    return Promise.resolve([...this.queue.values()].sort((a, b) => a.seq - b.seq));
  }

  putQueueItem(item: QueueItem): Promise<void> {
    this.queue.set(item.clientActionId, item);
    return Promise.resolve();
  }

  deleteQueueItem(clientActionId: string): Promise<void> {
    this.queue.delete(clientActionId);
    return Promise.resolve();
  }

  clearAll(): Promise<void> {
    this.session = null;
    this.route = null;
    this.ui = null;
    this.prefs = { ...DEFAULT_PREFS };
    this.queue.clear();
    return Promise.resolve();
  }
}

export function createStorage(): DeliveryStorage {
  if (typeof indexedDB === 'undefined') return new MemoryStorage();
  return new IndexedDbStorage();
}
